import { authedFetch, getAccessToken } from '@/providers/loginWithEmail/dataProvider';

export type QuoteAttachmentSource = 'attachment' | 'document' | 'drive';

export type QuoteAttachment = {
    id: string;
    name: string;
    filename: string;
    content_type: string;
    uploaded_at?: string;
    sharepoint_path?: string;
    /** Local disk attachment (default), SharePoint project document, or quote drive. */
    source?: QuoteAttachmentSource;
    quote_id?: number;
    drive_path?: string;
    project_file_id?: number;
};

const LOCAL_ATTACHMENT_ID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isLocalAttachmentId(id: string | undefined | null): boolean {
    return Boolean(id && LOCAL_ATTACHMENT_ID.test(id));
}

/** Persist relation + display fields only once the file is a ProjectFile. */
export function toEstimatorFileRef(file: QuoteAttachment): Record<string, unknown> {
    if (file.project_file_id != null) {
        return {
            project_file_id: file.project_file_id,
            name: file.name,
            filename: file.filename,
            content_type: file.content_type,
        };
    }
    return {
        id: file.id,
        name: file.name,
        filename: file.filename,
        content_type: file.content_type,
        ...(file.uploaded_at ? { uploaded_at: file.uploaded_at } : {}),
    };
}

export function fileRefsToQuoteAttachments(value: unknown): QuoteAttachment[] {
    if (!Array.isArray(value)) return [];
    return value.map(fileRefToQuoteAttachment).filter((file): file is QuoteAttachment => file != null);
}

export function fileRefToQuoteAttachment(value: unknown): QuoteAttachment | null {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const filename =
        typeof row.filename === 'string'
            ? row.filename
            : typeof row.name === 'string'
              ? row.name
              : '';
    const projectFileId =
        typeof row.project_file_id === 'number'
            ? row.project_file_id
            : typeof row.project_file_id === 'string' && /^\d+$/.test(row.project_file_id)
              ? Number(row.project_file_id)
              : undefined;
    const localId = typeof row.id === 'string' && isLocalAttachmentId(row.id) ? row.id : undefined;
    if (!filename && projectFileId == null && !localId) return null;
    const id = localId || (projectFileId != null ? String(projectFileId) : filename || 'file');
    return {
        id,
        project_file_id: projectFileId,
        name: typeof row.name === 'string' ? row.name : defaultAttachmentName(filename || id),
        filename: filename || `${id}`,
        content_type:
            typeof row.content_type === 'string' ? row.content_type : 'application/octet-stream',
        uploaded_at: typeof row.uploaded_at === 'string' ? row.uploaded_at : undefined,
        sharepoint_path: typeof row.sharepoint_path === 'string' ? row.sharepoint_path : undefined,
        source: localId ? 'attachment' : projectFileId != null ? 'document' : undefined,
    };
}

export type QuoteDriveItem = {
    name: string;
    path: string;
    is_folder: boolean;
    size?: number | null;
    last_modified?: string | null;
    content_type?: string | null;
    project_file_id?: number | null;
    locked: boolean;
    web_url?: string | null;
};

export type QuoteDriveList = {
    folder: string;
    path: string;
    parent_path: string | null;
    items: QuoteDriveItem[];
    configured: boolean;
};

export type QuoteDriveFolderNode = {
    name: string;
    path: string;
    children: QuoteDriveFolderNode[];
};

export type QuoteDriveTree = {
    folder: string;
    tree: QuoteDriveFolderNode[];
};

export type MapSharePointFilesResult = {
    quote_id: number;
    folder_path?: string | null;
    created: number;
    skipped: number;
    removed?: number;
};

export type QuoteAttachmentUploadResult = {
    attachment_id: string;
    filename: string;
    content_type: string;
    url: string;
};

export function parseUploadError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;
    const errors = obj.errors;
    if (errors && typeof errors === 'object') {
        const root = (errors as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    const detail = obj.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

export function defaultAttachmentName(filename: string): string {
    const base = filename.replace(/\.[^.]+$/, '').trim();
    return base || filename;
}

/** SharePoint forbids \ / : * ? " < > | in file names. */
const SHAREPOINT_FORBIDDEN = /[\\/:*?"<>|]/g;

export function sanitizeSharepointFilename(name: string): string {
    const raw = name.trim() || 'unnamed';
    const dot = raw.lastIndexOf('.');
    if (dot <= 0) {
        return raw.replace(SHAREPOINT_FORBIDDEN, '-').replace(/^-+|-+$/g, '') || 'unnamed';
    }
    const stem = raw.slice(0, dot).replace(SHAREPOINT_FORBIDDEN, '-').replace(/^-+|-+$/g, '') || 'unnamed';
    const ext = raw.slice(dot + 1).replace(SHAREPOINT_FORBIDDEN, '') || 'bin';
    return `${stem}.${ext}`;
}

/** plan.pdf → plan-calculated.pdf */
export function calculatedPdfFilename(original: string): string {
    const base = original.replace(/\.pdf$/i, '').trim() || original;
    return sanitizeSharepointFilename(`${base}-calculated.pdf`);
}

/** earth-session.png / earth-session → earth-session-calculated.png */
export function calculatedPngFilename(original: string): string {
    const base = original.replace(/\.png$/i, '').trim() || original;
    return sanitizeSharepointFilename(`${base}-calculated.png`);
}

export async function uploadQuoteAttachment(file: File): Promise<QuoteAttachmentUploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const res = await authedFetch('/quote-attachments/upload', {
        method: 'POST',
        body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(parseUploadError(data, 'Failed to upload attachment'));
    }
    return data as QuoteAttachmentUploadResult;
}

export async function deleteQuoteAttachment(attachmentId: string): Promise<void> {
    const res = await authedFetch(
        `/quote-attachments/${attachmentId}`,
        { method: 'DELETE' }
    );
    if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(parseUploadError(data, 'Failed to delete attachment'));
    }
}

export async function fetchQuoteAttachmentBlob(attachment: QuoteAttachment | string): Promise<Blob> {
    const row = typeof attachment === 'string' ? { id: attachment } : attachment;
    const id = row.id;
    const source = ('source' in row && row.source) ? row.source : 'attachment';
    const projectFileId = 'project_file_id' in row ? row.project_file_id : undefined;
    let path: string;
    // Prefer explicit source so drive previews match QuoteDriveExplorer (QUOTE_READ),
    // not /quotes/documents/... (PROJECT_FILE_READ) when both ids are present.
    if (source === 'drive') {
        const quoteId = 'quote_id' in row ? row.quote_id : undefined;
        const drivePath = ('drive_path' in row && row.drive_path) || id;
        if (quoteId == null) {
            throw new Error('Missing quote for drive file');
        }
        path = `/quotes/${quoteId}/drive/content?path=${encodeURIComponent(drivePath)}`;
    } else if (projectFileId != null) {
        path = `/quotes/documents/${projectFileId}/content`;
    } else if (source === 'document') {
        path = `/quotes/documents/${id}/content`;
    } else if (isLocalAttachmentId(id)) {
        path = `/quote-attachments/${id}`;
    } else {
        path = `/quote-attachments/${id}`;
    }
    const res = await authedFetch(path);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(parseUploadError(data, 'Failed to load attachment'));
    }
    return res.blob();
}

export async function downloadQuoteAttachment(attachment: QuoteAttachment): Promise<void> {
    const blob = await fetchQuoteAttachmentBlob(attachment);
    triggerBlobDownload(blob, attachment.filename);
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

export function driveZipDownloadFilename(paths: string[]): string {
    if (paths.length === 1) {
        const name = paths[0].split('/').filter(Boolean).pop() || 'download';
        return `${name}.zip`;
    }
    return 'selection.zip';
}

export function filterDriveItemsByName(
    items: QuoteDriveItem[],
    query: string,
): QuoteDriveItem[] {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => item.name.toLowerCase().includes(q));
}

export function quoteAttachmentServeUrl(attachmentId: string): string {
    const base = `${import.meta.env.VITE_APP_API_BACKEND}/quote-attachments/${attachmentId}`;
    const token = getAccessToken();
    if (import.meta.env.DEV && token) {
        return `${base}?access_token=${encodeURIComponent(token)}`;
    }
    return base;
}

export function quoteAttachmentThumbnailUrl(attachmentId: string): string {
    const base = `${import.meta.env.VITE_APP_API_BACKEND}/quote-attachments/${attachmentId}/thumbnail`;
    const token = getAccessToken();
    if (import.meta.env.DEV && token) {
        return `${base}?access_token=${encodeURIComponent(token)}`;
    }
    return base;
}

export async function fetchQuoteAttachmentThumbnailBlob(attachmentId: string): Promise<Blob> {
    const res = await authedFetch(
        `/quote-attachments/${attachmentId}/thumbnail`,
    );
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(parseUploadError(data, 'Failed to load thumbnail'));
    }
    return res.blob();
}

/** Cached JPEG thumb from `/quotes/{id}/drive/thumbnail` (Celery-generated). */
const driveThumbRequests = new Map<string, Promise<Blob>>();

function driveThumbKey(quoteId: number, drivePath: string): string {
    return `${quoteId}\0${drivePath}`;
}

export function invalidateQuoteDriveThumbnailCache(
    quoteId: number,
    drivePath?: string,
): void {
    if (drivePath != null) {
        driveThumbRequests.delete(driveThumbKey(quoteId, drivePath));
        return;
    }
    const prefix = `${quoteId}\0`;
    for (const key of [...driveThumbRequests.keys()]) {
        if (key.startsWith(prefix)) driveThumbRequests.delete(key);
    }
}

/** Clears the in-memory quote drive thumbnail cache (logout / tests). */
export function clearQuoteDriveThumbnailCache(): void {
    driveThumbRequests.clear();
}

export async function fetchQuoteDriveThumbnailBlob(
    quoteId: number,
    drivePath: string,
): Promise<Blob> {
    const key = driveThumbKey(quoteId, drivePath);
    const cached = driveThumbRequests.get(key);
    if (cached) return cached;

    const request = (async () => {
        const res = await authedFetch(
            `/quotes/${quoteId}/drive/thumbnail?path=${encodeURIComponent(drivePath)}`,
        );
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(parseUploadError(data, 'Failed to load thumbnail'));
        }
        return res.blob();
    })();

    driveThumbRequests.set(key, request);
    try {
        return await request;
    } catch (err) {
        driveThumbRequests.delete(key);
        throw err;
    }
}

export function isImageAttachment(contentType: string): boolean {
    return contentType === 'image/jpeg' || contentType === 'image/png';
}

export function isPdfAttachment(attachment: Pick<QuoteAttachment, 'content_type' | 'filename'>): boolean {
    return (
        attachment.content_type === 'application/pdf' ||
        attachment.filename.toLowerCase().endsWith('.pdf')
    );
}

/** Content type wins. The filename is only a fallback when the type is missing. */
export function attachmentPreviewKind(
    contentType: string | null | undefined,
    filename: string,
): 'image' | 'pdf' | 'other' {
    const type = (contentType || '').split(';')[0].trim().toLowerCase();
    if (type === 'application/pdf') return 'pdf';
    if (
        type === 'image/jpeg' ||
        type === 'image/png' ||
        type === 'image/webp' ||
        type === 'image/gif'
    ) {
        return 'image';
    }
    if (type && type !== 'application/octet-stream') return 'other';
    const name = filename.toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (/\.(png|jpe?g|webp|gif)$/.test(name)) return 'image';
    return 'other';
}

function attachmentsFromEstimationKey(
    estimationData: Record<string, unknown> | null | undefined,
    key: string,
): QuoteAttachment[] {
    const files = estimationData?.[key];
    if (!Array.isArray(files)) return [];
    return files.map(fileRefToQuoteAttachment).filter((file): file is QuoteAttachment => file != null);
}

export function relatedQuoteFilesFromEstimationData(
    estimationData: Record<string, unknown> | null | undefined,
): QuoteAttachment[] {
    return attachmentsFromEstimationKey(estimationData, 'related_quote_files');
}

export function viewFilesFromEstimationData(
    estimationData: Record<string, unknown> | null | undefined,
): QuoteAttachment[] {
    return attachmentsFromEstimationKey(estimationData, 'view_files');
}

function guessContentTypeFromName(name: string): string {
    const lower = name.toLowerCase();
    if (lower.endsWith('.pdf')) return 'application/pdf';
    if (lower.endsWith('.png')) return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
    return 'application/octet-stream';
}

export function driveItemToQuoteAttachment(
    quoteId: number,
    item: QuoteDriveItem,
): QuoteAttachment {
    return {
        id: item.path,
        name: item.name,
        filename: item.name,
        content_type: item.content_type || guessContentTypeFromName(item.name),
        source: 'drive',
        quote_id: quoteId,
        drive_path: item.path,
    };
}

/** Map a SharePoint/project document row into the shared preview attachment shape. */
export function documentToQuoteAttachment(doc: {
    id: number | string;
    title: string;
    file_path: string;
}): QuoteAttachment {
    const filename =
        doc.title?.trim() ||
        doc.file_path.split('/').filter(Boolean).pop() ||
        'file';
    return {
        id: String(doc.id),
        name: doc.title || filename,
        filename,
        content_type: guessContentTypeFromName(`${doc.title} ${doc.file_path}`),
        source: 'document',
    };
}
